import {
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
  trackAnalyticsEvent,
} from '@leagueplatform/analytics';

// Event sent when a capability nav link is clicked
export const sendCapabilityNavLinkEvent = (action: string) => () =>
  trackAnalyticsEvent(EVENT_NAME.NAVIGATION_TAB_CLICKED, {
    screen_name: 'Navigation Bar',
    detail: action,
  });

// Analytics properties for when a user option is clicked
export const getUserOptionAnalyticsProps = (action: string) => ({
  detail: 'option clicked',
  product_area: PRODUCT_AREA.SETTINGS,
  screen_name: SCREEN_NAMES.SETTINGS,
  sub_detail: action,
});

// Event sent when a user option is clicked in the user dropdown or mobile menu
export const sendUserOptionEvent = (action: string) =>
  trackAnalyticsEvent(
    EVENT_NAME.BUTTON_CLICKED,
    getUserOptionAnalyticsProps(action),
  );

// Screen loaded event tracking
export const sendScreenLoadedEvent = (
  screenName: string,
  productArea: string,
) =>
  trackAnalyticsEvent('screen loaded', {
    screen_name: screenName,
    product_area: productArea,
  });

// Button clicked event tracking
export const sendButtonClickedEvent = (
  screenName: string,
  productArea: string,
  detail: string,
  boolean?: boolean,
) =>
  trackAnalyticsEvent('button clicked', {
    screen_name: screenName,
    product_area: productArea,
    detail,
    ...(boolean !== undefined && { boolean }),
  });

// Account Setup Events
export const sendAccountSetupScreenLoadedEvent = () =>
  sendScreenLoadedEvent('account setup', 'account setup');

export const sendAccountSetupButtonClickedEvent = (
  detail: string,
  boolean?: boolean,
  screenName?: string,
) =>
  sendButtonClickedEvent(
    screenName || 'account setup',
    'account setup',
    detail,
    boolean,
  );

// Onboarding Events
export const sendOnboardingScreenLoadedEvent = (screenName: string) =>
  sendScreenLoadedEvent(screenName, 'onboarding');

export const sendOnboardingButtonClickedEvent = (
  screenName: string,
  detail: string,
) => sendButtonClickedEvent(screenName, 'onboarding', detail);

// Settings Events
export const sendSettingsScreenLoadedEvent = (screenName: string) =>
  sendScreenLoadedEvent(screenName, 'settings');

export const sendSettingsButtonClickedEvent = (
  screenName: string,
  detail: string,
) => sendButtonClickedEvent(screenName, 'settings', detail);

// Profile Settings Events
export const sendProfileSettingsButtonClickedEvent = (
  detail: string,
  boolean?: boolean,
) => sendButtonClickedEvent('profile settings', 'settings', detail, boolean);

// Communication Events
export const sendCommunicationButtonClickedEvent = (
  detail: string,
  boolean?: boolean,
) => sendButtonClickedEvent('edit communications', 'settings', detail, boolean);

// Home Events
export const sendHomeButtonClickedEvent = (detail: string) =>
  sendButtonClickedEvent('home', 'home', detail);

// Care Events
export const sendCareButtonClickedEvent = (detail: string) =>
  sendButtonClickedEvent('get care', 'get care', detail);

// Premium Payment Events
export const sendPDPPremiumPaymentScreenLoadedEvent = () =>
  sendScreenLoadedEvent('pdp premium payment', 'home');

export const sendSupplementalPremiumPaymentScreenLoadedEvent = () =>
  sendScreenLoadedEvent('supplemental premium payment', 'settings');

export const sendMedicarePrescriptionPaymentPlanScreenLoadedEvent = () =>
  sendScreenLoadedEvent('medicare prescription payment plan', 'get care');

export const sendPaymentCompletedScreenLoadedEvent = () =>
  sendScreenLoadedEvent('payment completed', 'settings');

export const sendAutomaticPaymentSetupScreenLoadedEvent = () =>
  sendScreenLoadedEvent('automatic payments setup', 'settings');

export const sendPremiumPaymentHistoryScreenLoadedEvent = () =>
  sendScreenLoadedEvent('premium payment history', 'settings');

// Landing Page Events
export const sendLandingPageScreenLoadedEvent = () =>
  sendScreenLoadedEvent('landing page', 'landing');

// Error Analytics Events (Items 108-115)
export const sendErrorScreenLoadedEvent = (screenName: string) =>
  trackAnalyticsEvent('screen loaded', {
    product_area: 'foundation',
    sub_product_area: 'application error handling',
    screen_name: screenName,
  });

export const sendErrorButtonClickedEvent = (
  screenName: string,
  detail: string,
) =>
  trackAnalyticsEvent('button clicked', {
    product_area: 'foundation',
    sub_product_area: 'application error handling',
    screen_name: screenName,
    detail,
  });

// Account Setup Error Modal Events (Items 114-115)
export const sendAccountSetupErrorButtonClickedEvent = (detail: string) =>
  trackAnalyticsEvent('button clicked', {
    product_area: 'foundation',
    sub_product_area: 'account setup',
    screen_name: 'profile update error',
    detail,
  });

// Helper function to get detail from communication preference ID
export const getCommunicationDetailFromId = (
  id: string,
  booleanValue?: boolean,
): string => {
  const detailMap: Record<string, string> = {
    STR_PAPERLESS: booleanValue ? 'yes paperless' : 'no paperless',
    STR_PERSONALIZED_EMAIL: 'personalized email',
    STR_TIPS_AND_RECOMMENDATIONS: 'tips and recommendations email',
    STR_PRODUCT_UPDATES: 'product updates email',
    STR_TEXT: 'text messages',
    STR_LANGUAGE: 'spanish',
  };
  return detailMap[id] || id;
};
